import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import ChargePointSelectors from 'Stores/ChargePoint/Selectors'
import AuthSelectors from 'Stores/Auth/Selectors'
import ChargePointActions from 'Stores/ChargePoint/Actions'
import RequestState from 'Enums/RequestState'
import Styles from './ChargingSessionLogsList.module.css'
import ChargePointLogListItem from 'Components/ChargePointLogListItem/ChargePointLogListItem'
import ChargePointLogListItemSkeleton from 'Components/ChargePointLogListItem/ChargePointLogListItemSkeleton'
import { EmptyLogs, IFText } from 'Components'
import { NoLogsFilter, IFsvg } from 'Components'
import { Virtuoso } from 'react-virtuoso'
import { Colors } from 'Theme'
import { isPropsMatch } from 'Utils/PropsMatch'
import { useOverlayScrollbars } from 'overlayscrollbars-react'
import 'overlayscrollbars/overlayscrollbars.css'
import { styled } from '@mui/system'

const NewLogsIndicator = styled('div')({
  boxShadow: Colors.shadow,
  backgroundColor: Colors.LogsBackGroundIndicator,
  '&:hover': {
    backgroundColor: Colors.LogsBackGroundIndicatorHover,
  },
})

const ChargingSessionLogsList = ({
  chargingSessionId,
  fetchChargingSessionLogs,
  chargingSessionLogs,
  chargePointLogsFirstItemIndex,
  fetchChargingSessionLogsRequestState,
  paginationOffset,
  chargingSessionLogsFilter,
  prependChargingSessionLogList,
  bufferedChargingSessionLogs,
}) => {
  const [empty, setEmpty] = useState(true)
  const [didBufferLogs, setDidBufferLogs] = useState(false)
  const isAtTopRef = useRef(false)
  const virtuosoRef = useRef(null)
  const chargePointLogFilterRef = useRef([])
  const prevScrollTop = useRef(0)
  const prevScrollHeight = useRef(0)
  const autoScroll = useRef(false)

  const Footer = () => {
    return paginationOffset &&
      chargingSessionLogs &&
      chargingSessionLogs.length !== 0 ? (
      <div className={Styles.WaypointContainer}>
        <ChargePointLogListItemSkeleton className={Styles.item} />
        <ChargePointLogListItemSkeleton className={Styles.item} />
      </div>
    ) : null
  }

  const options = {
    scrollbars: {
      visibility: 'auto',
      autoHide: 'move',
      autoHideDelay: 1000,
    },
  }
  const rootRef = useRef(null)
  const [scroller, setScroller] = useState(null)
  const [initialize, osInstance] = useOverlayScrollbars({
    options,
    defer: true,
  })
  useEffect(() => {
    const { current: root } = rootRef
    if (scroller && root) {
      initialize({
        target: root,
        elements: {
          viewport: scroller,
        },
      })
    }
    return () => osInstance()?.destroy()
  }, [scroller, initialize, osInstance])
  const loadMoreData = () => {
    if (fetchChargingSessionLogsRequestState === RequestState.loadMoreData)
      return

    fetchChargingSessionLogs(
      chargingSessionId,
      paginationOffset,
      chargingSessionLogsFilter,
    )
  }
  const checkFilterEmpty = (newFilter) => {
    const emptyFlag = !newFilter.some((filter) => filter.value.length !== 0)
    if (emptyFlag) setEmpty(true)
    else setEmpty(false)
  }
  useEffect(() => {
    checkFilterEmpty(chargingSessionLogsFilter)
    chargePointLogFilterRef.current = chargingSessionLogsFilter
  }, [chargingSessionLogsFilter])

  useEffect(() => {
    if (bufferedChargingSessionLogs.length > 0) setDidBufferLogs(true)
  }, [bufferedChargingSessionLogs])

  function scrollToTop() {
    setTimeout(() => {
      if (isAtTopRef.current) {
        virtuosoRef.current?.scrollToIndex({
          index: 0,
          behavior: 'smooth',
          align: 'start',
        })
      }
    }, 50)
  }

  return (
    <div
      className={Styles.container}
      {...((empty && chargingSessionLogs.length === 0) ||
      (!empty && chargingSessionLogs.length === 0)
        ? { style: { width: '100%', height: '100%' } }
        : {})}
    >
      <NewLogsIndicator
        className={`
    ${Styles.newLogsIndicator}
    ${
      bufferedChargingSessionLogs.length > 0
        ? Styles.animateInNewLogsIndicator
        : didBufferLogs
        ? Styles.animateOutNewLogsIndicator
        : ''
    }
  `}
        onClick={() => {
          autoScroll.current = true
          prependChargingSessionLogList([...bufferedChargingSessionLogs])
          isAtTopRef.current = true
          scrollToTop()
        }}
      >
        <IFsvg.ArrowUp height={20} width={20} fill={Colors.black} />
        <IFText
          className={Styles.newLogsText}
          style={{ color: Colors.text }}
        >{`${bufferedChargingSessionLogs.length} new logs`}</IFText>
      </NewLogsIndicator>

      {fetchChargingSessionLogsRequestState === RequestState.LOADING &&
      paginationOffset === 0 ? (
        <div className={Styles.VirtuosoContainer} ref={rootRef}>
          <div className={Styles.skeletonWrapper}>
            {[...Array(15)].map((e, index) => (
              <ChargePointLogListItemSkeleton
                className={Styles.item}
                key={`ChargePointLogListItemSkeleton ${index}`}
              />
            ))}
          </div>
        </div>
      ) : chargingSessionLogs.length === 0 ? (
        empty ? (
          <EmptyLogs />
        ) : (
          <NoLogsFilter />
        )
      ) : (
        <div className={Styles.VirtuosoContainer} ref={rootRef}>
          <Virtuoso
            ref={virtuosoRef}
            scrollerRef={(ref) => {
              if (ref) {
                setScroller(ref)
              }
            }}
            data={chargingSessionLogs}
            endReached={
              paginationOffset &&
              chargingSessionLogs &&
              chargingSessionLogs.length !== 0
                ? loadMoreData
                : () => {}
            }
            increaseViewportBy={480}
            itemContent={(index, chargePointLog) => {
              return (
                <div key={index}>
                  {chargePointLog.responseMessage ? (
                    <ChargePointLogListItem
                      key={`ChargePointLogListItemResponse ${index}`}
                      isIncoming={!chargePointLog.isIncoming}
                      action={chargePointLog.messageAction}
                      date={chargePointLog.updatedAt}
                      callId={chargePointLog.messageId}
                      message={
                        chargePointLog.responseMessage
                          ? chargePointLog.responseMessage.message
                          : undefined
                      }
                    />
                  ) : null}
                  {chargePointLog.requestMessage ? (
                    <ChargePointLogListItem
                      key={`ChargePointLogListItemRequest ${index}`}
                      isIncoming={chargePointLog.isIncoming}
                      action={chargePointLog.messageAction}
                      date={chargePointLog.createdAt}
                      callId={chargePointLog.messageId}
                      message={
                        chargePointLog.requestMessage
                          ? chargePointLog.requestMessage.message
                          : undefined
                      }
                    />
                  ) : null}
                </div>
              )
            }}
            firstItemIndex={chargePointLogsFirstItemIndex}
            atTopStateChange={(isAtTop) => {
              if (isAtTop && bufferedChargingSessionLogs.length > 0) {
                prependChargingSessionLogList([...bufferedChargingSessionLogs])
                isAtTopRef.current = false
                return
              }
              if (isAtTop) isAtTopRef.current = isAtTop
            }}
            onScroll={(event) => {
              if (event.target.scrollTop === 0) {
                setTimeout(() => {
                  if (event.target.scrollTop === 0) autoScroll.current = false
                }, 150)
              }
              if (prevScrollHeight.current === event.target.scrollHeight) {
                //prepend
                if (prevScrollTop.current < event.target.scrollTop) {
                  //Scroll Down

                  if (!autoScroll.current) isAtTopRef.current = false
                }
              } else {
                if (prevScrollTop.current > event.target.scrollTop) {
                  prevScrollTop.current +=
                    event.target.scrollHeight - prevScrollHeight.current
                  prevScrollHeight.current = event.target.scrollHeight
                  return
                }
              }

              prevScrollTop.current = event.target.scrollTop
              prevScrollHeight.current = event.target.scrollHeight
            }}
            components={{ Footer: Footer }}
          />
        </div>
      )}
    </div>
  )
}

function mapDispatchToProps(dispatch) {
  return {
    fetchChargingSessionLogs: (filter, offset, chargePointId) =>
      dispatch(
        ChargePointActions.fetchChargingSessionLogs(
          filter,
          offset,
          chargePointId,
        ),
      ),
    prependChargingSessionLogList: (chargePointLogs) =>
      dispatch(
        ChargePointActions.prependChargingSessionLogList(chargePointLogs),
      ),
    appendBufferChargingSessionLogList: (chargePointLogs) =>
      dispatch(
        ChargePointActions.appendBufferChargingSessionLogList(chargePointLogs),
      ),
    setChargingSessionLogPaginationOffset: (offset) =>
      dispatch(
        ChargePointActions.setChargingSessionLogPaginationOffset(offset),
      ),
  }
}

const mapStateToProps = (state) => ({
  chargingSessionLogs: ChargePointSelectors.getChargingSessionLogs(state),
  chargePointLogsFirstItemIndex:
    ChargePointSelectors.getChargePointLogsFirstItemIndex(state),
  bufferedChargingSessionLogs:
    ChargePointSelectors.getBufferedChargingSessionLogs(state),
  fetchChargingSessionLogsRequestState:
    ChargePointSelectors.getFetchChargingSessionLogsRequestState(state),
  paginationOffset:
    ChargePointSelectors.getChargingSessionLogsPaginationOffset(state),
  chargingSessionLogsFilter:
    ChargePointSelectors.getChargingSessionLogsFilter(state),
  operator: AuthSelectors.getOperator(state),
  chargePointUid: ChargePointSelectors.getChargePointUid(state),
})

ChargingSessionLogsList.propTypes = {
  fetchChargePointLogList: PropTypes.func,
  fetchChargingSessionLogsRequestState: PropTypes.number,
  paginationOffset: PropTypes.number,
  chargingSessionLogsFilter: PropTypes.arrayOf(PropTypes.object),
}
function shouldSkipRender(prevProps, nextProps) {
  return isPropsMatch(prevProps, nextProps, [
    'fetchChargePointLogList',
    'chargePointLogsFirstItemIndex',
    'fetchChargingSessionLogsRequestState',
    'paginationOffset',
    'chargingSessionLogsFilter',
    'ascending',
    'prependChargingSessionLogList',
    'bufferedChargingSessionLogs',
    'bufferedChargingSessionLogs',
  ])
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(React.memo(ChargingSessionLogsList, shouldSkipRender))
